import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Container, makeStyles, Typography } from '@material-ui/core';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import { FieldArray, FieldArrayRenderProps, useFormikContext } from 'formik';
import { Organization } from 'types/schema-generated-types';
import StepHeader from '../StepHeader';
import { StudentTableRow } from '../StudentForm/StudentTableRow';

interface AddStudentsStepProps {
  index: number;
  organization?: Organization;
  usernames?: string[];
}

const AddStudentsStep = ({ index, usernames }: AddStudentsStepProps) => {
  const classes = useStyles();
  const { t } = useTranslation('AddStudentsStep');
  const [focused, setFocused] = useState(-1);
  const [touchedRows, setTouchedRows] = useState<{ [key: number]: boolean }>({});
  const form: any = useFormikContext();

  const handleRowFocus = (index: number) => {
    if (index === focused) return;
    if (focused === -1) {
      setFocused(index);
      return;
    }

    const rowValues = Object.values(form.values.students[focused]);
    const touched = rowValues.some(x => !!x);
    setTouchedRows({ ...touchedRows, [focused]: touched });
  };

  return (
    <FieldArray name="students">
      {({ form, push, remove }: FieldArrayRenderProps) => {
        const handleAddRow = () => {
          push({
            firstName: undefined,
            lastName: undefined,
            username: undefined,
            secretCode: undefined,
            email: undefined,
            gradeLevel: undefined,
            group: undefined
          });
        };

        return (
          <div className={classes.root}>
            <StepHeader
              title={`${index}. ${t('Add Students')}`}
              subtitle={t(
                'Please fill out the table below with the necessary information about your students'
              )}
              maxWidth="lg"
            />
            <Container className={classes.tableContainer}>
              <table className={classes.table}>
                <thead>
                  <tr>
                    <th className={classes.th}>
                      <Typography className={classes.thLabel}>{t('First Name')}</Typography>
                    </th>
                    <th className={classes.th}>
                      <Typography className={classes.thLabel}>{t('Last Name')}</Typography>
                    </th>
                    <th className={classes.th}>
                      <Typography className={classes.thLabel}>{t('Username')}</Typography>
                    </th>
                    <th className={classes.th}>
                      <Typography className={classes.thLabel}>{t('Secret Code')}</Typography>
                    </th>
                    <th className={classes.th}>
                      <Typography className={classes.thLabel}>{t('Email Address')}</Typography>
                    </th>
                    <th className={classes.th}>
                      <Typography className={classes.thLabel}>{t('Grade')}</Typography>
                    </th>
                    <th className={classes.th}></th>
                  </tr>
                </thead>
                <tbody>
                  {form.values?.students?.map((student: object, index: number) => (
                    <StudentTableRow
                      onFocus={handleRowFocus}
                      rowTouched={!!touchedRows[index]}
                      usernames={usernames}
                      key={`student_index_${index}`}
                      index={index}
                      remove={remove}
                    />
                  ))}
                </tbody>
              </table>
              <div className={classes.footer}>
                <Button
                  variant="contained"
                  color="secondary"
                  size="small"
                  disableElevation
                  onClick={handleAddRow}
                  aria-label={t('Add New Student')}
                  startIcon={<AddCircleIcon />}
                >
                  {t('Add New Student')}
                </Button>
              </div>
            </Container>
          </div>
        );
      }}
    </FieldArray>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  tableContainer: {
    paddingLeft: theme.spacing() - 1,
    paddingRight: theme.spacing() - 1
  },
  table: {
    marginTop: theme.spacing(2),
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: `${theme.spacing(2)}px 0`
  },
  th: {
    textAlign: 'left',
    borderBottom: '2px solid white'
  },
  thLabel: {
    fontWeight: 500
  },
  form: {
    width: '100%'
  },
  footer: {
    display: 'flex',
    justifyContent: 'center',
    paddingBottom: theme.spacing(8)
  }
}));

export default AddStudentsStep;
